import * as React from 'react';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';

import {api} from 'nutshell-core/api';
import {Routing} from 'nutshell-core/routing';
import type {HttpErrorObject} from 'nutshell-core/utils/errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {FeatureStatusCard} from 'shells/card/feature-status-card';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {colors} from 'shells/colors/colors';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Stack} from 'shells/layout/stack/stack';
import {RightFrame, FooterWithControls, ModalMain} from 'shells/modal/step-modal/layout';
import {Body} from 'shells/typography/body';
import {ModalHeader} from 'shells/typography/modal-header';

import {getServiceNoun, getServiceTypeIconComponent} from '../../../calls/helpers';

import {ERROR_CODE_MAP, getErrorFlashBannerFromCode} from './error-handling-utils';
import type {SetupError} from './error-handling-utils';
import type {ErrorObject} from './use-get-click-to-call-validation-status';
import {useGetClickToCallValidationStatus} from './use-get-click-to-call-validation-status';

import styles from './click-to-call-validate-code.module.css';

type Props = {
    name: string;
    phoneNumber: string;
    verificationCode: string;
    history: RouterHistory;
};

export function ClickToCallValidateCode(props: Props) {
    const {name, phoneNumber, verificationCode, history} = props;

    const [isRetrying, setIsRetrying] = React.useState<boolean>(false);
    const [errorRetrying, setErrorRetrying] = React.useState<SetupError | null | undefined>(null);

    const {isValidated, error} = useGetClickToCallValidationStatus(
        phoneNumber,
        name,
        verificationCode
    );

    const serviceNoun = getServiceNoun('CLICKTOCALL');

    React.useEffect(() => {
        if (isValidated === true) {
            const updatedParams = {
                name: name,
                phoneNumber: phoneNumber,
            } as const;

            const nextPage = `/connect-phone-integration/click-to-call/connected?${Routing.param(
                updatedParams
            )}`;

            history.push(nextPage);
        }
    }, [isValidated, history, error, name, phoneNumber]);

    const handleCallAgain = () => {
        if (isValidated === true) {
            return;
        }

        setErrorRetrying(null);
        setIsRetrying(true);

        api.post('/phones', {
            data: {
                type: 'phone',
                e164number: phoneNumber,
                name: name,
            },
        })
            // @ts-expect-error
            .then((response: Response): Promise<any> => response.json())
            .then((newVerificationCode: string) => {
                history.replace(
                    `/connect-phone-integration/click-to-call/validate-code?name=${name}&phoneNumber=${phoneNumber}&verificationCode=${newVerificationCode}`
                );
            })
            .catch((errorResponse: HttpErrorObject) => {
                errorResponse.httpResponse.json().then((errorCode: string) => {
                    setErrorRetrying(ERROR_CODE_MAP[errorCode] || null);
                });
            })
            .finally(() => {
                setIsRetrying(false);
            });
    };

    return (
        <RightFrame>
            <ModalMain>
                <ModalHeader>Nutshell {serviceNoun} setup</ModalHeader>
                <Stack spacing={20}>
                    <FeatureStatusCard
                        icon={getServiceTypeIconComponent('CLICKTOCALL')}
                        iconFill={colors.orange}
                        title={`Nutshell ${serviceNoun}`}
                        subtitle={name}
                        tertiaryData={phoneNumber}
                        // @ts-expect-error
                        statusBadge={getStatusBadgeProps(isValidated, error)}
                    />
                    <div>
                        <Body>
                            We are calling you now. You will be prompted to enter this 6 digit code:
                        </Body>
                        <div className={styles['verification-code']}>
                            <Body size='xlarge' isBold={true} color='blue-dk'>
                                {verificationCode}
                            </Body>
                        </div>
                    </div>
                    <div className='flex flex-dir-col gap-8'>
                        <Body>Didn‘t receive a phone call?</Body>
                        <div className='flex flex-dir-row justify-sb gap-8'>
                            <Button
                                variant='secondary'
                                size='big'
                                isFullWidth={true}
                                onClick={() => {
                                    window.open('https://support.nutshell.com/', '_blank');
                                }}
                            >
                                Contact customer support
                            </Button>
                            <Button
                                variant='secondary'
                                size='big'
                                isFullWidth={true}
                                onClick={handleCallAgain}
                                disabled={isRetrying}
                            >
                                Call me again
                            </Button>
                        </div>
                    </div>
                    {!errorRetrying && error && (
                        <FlashBanner type='error' className='pb-24'>
                            <Body color='error'>{error.errorMessage}</Body>
                        </FlashBanner>
                    )}
                    {errorRetrying && getErrorFlashBannerFromCode(errorRetrying)}
                </Stack>
            </ModalMain>
            <FooterWithControls
                controls={<SlideshowControls selectedSlide={1} slideCount={4} />}
                buttons={
                    <React.Fragment>
                        <Button variant='text' onClick={history.goBack}>
                            Back
                        </Button>
                        <SaveButton
                            variant='primary'
                            size='big'
                            buttonText='Call me'
                            savingText='Calling…'
                            isSaving={isValidated === null}
                            disabled={isValidated === null || isValidated === true}
                            errorSaving={Boolean(error)}
                            // @ts-expect-error
                            onClick={handleCallAgain}
                        />
                    </React.Fragment>
                }
            />
        </RightFrame>
    );
}

function getStatusBadgeProps(isValidated?: boolean | null, error?: ErrorObject | null) {
    if (isValidated === null) {
        return {
            text: 'Verifying…',
            level: 'warning',
        };
    } else if (error !== null || isValidated === false) {
        return {
            text: 'Verification failed',
            level: 'alert',
        };
    }

    return {
        text: 'Verified',
        level: 'success',
    };
}
